import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X, Truck, Package } from 'lucide-react';
import '../styles/MasterForms.css';

const DRSTransactionEntry = () => {
  // Master DRS State
  const [drsData, setDRSData] = useState({
    drsid: null,
    companyid: 1,
    drsno: '',
    drsdate: new Date().toISOString().split('T')[0],
    despatchdate: new Date().toISOString().split('T')[0],
    deliveredby_empid: '',
    checkedby_empid: '',
    vehicleid: '',
    totalamount: 0,
    remarks: ''
  });

  // Current Detail Line State
  const [detailData, setDetailData] = useState({
    drsdetailid: null,
    consignmentid: 0,
    awbno: '',
    couriercharge: 0,
    materialcost: 0,
    cc_received: 0,
    cc_paymentmode: '',
    cc_bankname: '',
    cc_chequeno: '',
    cc_chequedate: '',
    mc_received: 0,
    mc_paymentmode: '',
    mc_bankname: '',
    mc_chequeno: '',
    mc_chequedate: '',
    collectedby_empid: '',
    receivername: '',
    receivedtime: new Date().toISOString().slice(0, 16),
    deliverystatus: 'PD',
    remarks: ''
  });

  // Detail Lines Array
  const [details, setDetails] = useState([]);

  // Dropdown Data
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [paymentModes] = useState([
    { modecode: 'CSH', modename: 'Cash' },
    { modecode: 'CHQ', modename: 'Cheque' },
    { modecode: 'CRD', modename: 'Card' },
    { modecode: 'BT', modename: 'Bank Transfer' },
    { modecode: 'CR', modename: 'Credit' }
  ]);
  const [deliveryStatuses] = useState([
    { statuscode: 'DL', statusname: 'Delivered' },
    { statuscode: 'PD', statusname: 'Pending' },
    { statuscode: 'RT', statusname: 'Returned' },
    { statuscode: 'CN', statusname: 'Cancelled' },
    { statuscode: 'HD', statusname: 'On Hold' }
  ]);

  // UI State
  const [editingDetail, setEditingDetail] = useState(false);
  const [editingDetailIndex, setEditingDetailIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('new');

  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      // Load Employees
      const empResponse = await fetch('/api/employees.php?companyid=1&isactive=1');
      const empData = await empResponse.json();
      if (empData.success) {
        setEmployees(empData.data || []);
      }

      // Load Vehicles
      const vehResponse = await fetch('/api/vehicles.php?companyid=1&isactive=1');
      const vehData = await vehResponse.json();
      if (vehData.success) {
        setVehicles(vehData.data || []);
      }
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  };

  const handleNewDRS = () => {
    setMode('new');
    setDRSData({
      drsid: null,
      companyid: 1,
      drsno: '',
      drsdate: new Date().toISOString().split('T')[0],
      despatchdate: new Date().toISOString().split('T')[0],
      deliveredby_empid: '',
      checkedby_empid: '',
      vehicleid: '',
      totalamount: 0,
      remarks: ''
    });
    setDetails([]);
    resetDetailForm();
  };

  const resetDetailForm = () => {
    setDetailData({
      drsdetailid: null,
      consignmentid: 0,
      awbno: '',
      couriercharge: 0,
      materialcost: 0,
      cc_received: 0,
      cc_paymentmode: '',
      cc_bankname: '',
      cc_chequeno: '',
      cc_chequedate: '',
      mc_received: 0,
      mc_paymentmode: '',
      mc_bankname: '',
      mc_chequeno: '',
      mc_chequedate: '',
      collectedby_empid: '',
      receivername: '',
      receivedtime: new Date().toISOString().slice(0, 16),
      deliverystatus: 'PD',
      remarks: ''
    });
    setEditingDetail(false);
    setEditingDetailIndex(null);
  };

  const calculateDetailTotal = () => {
    const ccReceived = parseFloat(detailData.cc_received) || 0;
    const mcReceived = parseFloat(detailData.mc_received) || 0;
    return ccReceived + mcReceived;
  };

  const handleAddDetail = () => {
    if (!detailData.awbno.trim()) {
      alert('AWB number is required');
      return;
    }

    if (editingDetail) {
      const updatedDetails = [...details];
      updatedDetails[editingDetailIndex] = { ...detailData };
      setDetails(updatedDetails);
    } else {
      const newDetail = { ...detailData, drsdetailid: Date.now() };
      setDetails([...details, newDetail]);
    }

    updateMasterTotal();
    resetDetailForm();
  };

  const handleEditDetail = (detail, index) => {
    setDetailData({ ...detail });
    setEditingDetail(true);
    setEditingDetailIndex(index);
  };

  const handleDeleteDetail = (index) => {
    if (window.confirm('Are you sure you want to delete this detail?')) {
      const updatedDetails = details.filter((_, i) => i !== index);
      setDetails(updatedDetails);
      
      // Recalculate total after deletion
      let total = 0;
      updatedDetails.forEach(detail => {
        total += (parseFloat(detail.cc_received) || 0) + (parseFloat(detail.mc_received) || 0);
      });
      setDRSData(prev => ({ ...prev, totalamount: total }));
    }
  };

  const updateMasterTotal = () => {
    let total = 0;
    details.forEach(detail => {
      total += (parseFloat(detail.cc_received) || 0) + (parseFloat(detail.mc_received) || 0);
    });
    
    if (!editingDetail) {
      total += (parseFloat(detailData.cc_received) || 0) + (parseFloat(detailData.mc_received) || 0);
    }
    
    setDRSData(prev => ({ ...prev, totalamount: total }));
  };

  const validateDRS = () => {
    if (!drsData.drsdate) {
      alert('DRS date is required');
      return false;
    }
    
    if (!drsData.despatchdate) {
      alert('Despatch date is required');
      return false;
    }
    
    if (details.length === 0) {
      alert('Please add at least one detail line');
      return false;
    }
    
    return true;
  };

  const handleSaveDRS = async () => {
    if (!validateDRS()) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/drs.php', {
        method: mode === 'edit' ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...drsData,
          details: details
        })
      });

      const data = await response.json();

      if (data.success) {
        alert(`DRS ${mode === 'edit' ? 'updated' : 'created'} successfully!\nDRS No: ${data.data?.drsno || drsData.drsno}`);
        if (mode === 'new') {
          setDRSData(prev => ({ ...prev, drsno: data.data.drsno, drsid: data.data.drsid }));
          setMode('edit');
        }
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving DRS:', error);
      alert('Error saving DRS. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPaymentModeName = (code) => {
    const mode = paymentModes.find(m => m.modecode === code);
    return mode ? mode.modename : code;
  };

  const getDeliveryStatusName = (code) => {
    const status = deliveryStatuses.find(s => s.statuscode === code);
    return status ? status.statusname : code;
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>Daily Run Sheet (DRS)</h2>
          <p className="subtitle">Delivery runs & payment collections</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-primary" onClick={handleNewDRS}>
            <Plus size={20} />
            New DRS
          </button>
          <button 
            className="btn-success" 
            onClick={handleSaveDRS}
            disabled={loading || mode === 'view'}
          >
            <Save size={20} />
            {loading ? 'Saving...' : 'Save DRS'}
          </button>
        </div>
      </div>

      {/* DRS Header */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Truck size={20} color="#667eea" />
          DRS Information
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
          <div className="form-group">
            <label>DRS Number</label>
            <input
              type="text"
              value={drsData.drsno || 'Auto-generated'}
              disabled
              style={{ background: '#f3f4f6', fontFamily: 'monospace', fontWeight: 'bold' }}
            />
          </div>

          <div className="form-group">
            <label>DRS Date <span className="required">*</span></label>
            <input
              type="date"
              value={drsData.drsdate}
              onChange={(e) => setDRSData({ ...drsData, drsdate: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Despatch Date <span className="required">*</span></label>
            <input
              type="date"
              value={drsData.despatchdate}
              onChange={(e) => setDRSData({ ...drsData, despatchdate: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Total Collected</label>
            <input
              type="text"
              value={drsData.totalamount.toFixed(2) + ' SAR'}
              disabled
              style={{ background: '#f0fdf4', fontWeight: 'bold', color: '#16a34a' }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginTop: '15px' }}>
          <div className="form-group">
            <label>Delivered By</label>
            <select
              value={drsData.deliveredby_empid}
              onChange={(e) => setDRSData({ ...drsData, deliveredby_empid: e.target.value })}
            >
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.employeeid} value={emp.employeeid}>
                  {emp.employeename}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Checked By</label>
            <select
              value={drsData.checkedby_empid}
              onChange={(e) => setDRSData({ ...drsData, checkedby_empid: e.target.value })}
            >
              <option value="">-- Select Employee --</option>
              {employees.map(emp => (
                <option key={emp.employeeid} value={emp.employeeid}>
                  {emp.employeename}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Vehicle</label>
            <select
              value={drsData.vehicleid}
              onChange={(e) => setDRSData({ ...drsData, vehicleid: e.target.value })}
            >
              <option value="">-- Select Vehicle --</option>
              {vehicles.map(veh => (
                <option key={veh.VehicleID} value={veh.VehicleID}>
                  {veh.RegistrationNo}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Detail Entry */}
      {mode !== 'view' && (
        <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Package size={20} color="#667eea" />
            Consignment Entry
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
            <div className="form-group">
              <label>AWB Number <span className="required">*</span></label>
              <input
                type="text"
                value={detailData.awbno}
                onChange={(e) => setDetailData({ ...detailData, awbno: e.target.value.toUpperCase() })}
                placeholder="AWB123456"
              />
            </div>

            <div className="form-group">
              <label>Receiver Name</label>
              <input
                type="text"
                value={detailData.receivername}
                onChange={(e) => setDetailData({ ...detailData, receivername: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={detailData.deliverystatus}
                onChange={(e) => setDetailData({ ...detailData, deliverystatus: e.target.value })}
              >
                {deliveryStatuses.map(s => (
                  <option key={s.statuscode} value={s.statuscode}>{s.statusname}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Courier Charge */}
          <div style={{ border: '2px solid #3b82f6', borderRadius: '8px', padding: '15px', marginTop: '15px' }}>
            <h4 style={{ marginBottom: '10px', color: '#3b82f6' }}>💵 Courier Charge</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
              <div className="form-group">
                <label>Amount</label>
                <input type="number" step="0.01" value={detailData.couriercharge}
                  onChange={(e) => setDetailData({ ...detailData, couriercharge: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Received</label>
                <input type="number" step="0.01" value={detailData.cc_received}
                  onChange={(e) => setDetailData({ ...detailData, cc_received: e.target.value })} 
                  style={{ fontWeight: 'bold' }} />
              </div>
              <div className="form-group">
                <label>Mode</label>
                <select value={detailData.cc_paymentmode}
                  onChange={(e) => setDetailData({ ...detailData, cc_paymentmode: e.target.value })}>
                  <option value="">Select</option>
                  {paymentModes.map(m => <option key={m.modecode} value={m.modecode}>{m.modename}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Collected By</label>
                <select value={detailData.collectedby_empid}
                  onChange={(e) => setDetailData({ ...detailData, collectedby_empid: e.target.value })}>
                  <option value="">Select</option>
                  {employees.map(e => <option key={e.employeeid} value={e.employeeid}>{e.employeename}</option>)}
                </select>
              </div>
            </div>
            {detailData.cc_paymentmode === 'CHQ' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '10px', background: '#dbeafe', padding: '10px', borderRadius: '5px' }}>
                <input placeholder="Bank Name" value={detailData.cc_bankname}
                  onChange={(e) => setDetailData({ ...detailData, cc_bankname: e.target.value })} />
                <input placeholder="Cheque No" value={detailData.cc_chequeno}
                  onChange={(e) => setDetailData({ ...detailData, cc_chequeno: e.target.value })} />
                <input type="date" value={detailData.cc_chequedate}
                  onChange={(e) => setDetailData({ ...detailData, cc_chequedate: e.target.value })} />
              </div>
            )}
          </div>

          {/* Material Cost */}
          <div style={{ border: '2px solid #10b981', borderRadius: '8px', padding: '15px', marginTop: '15px' }}>
            <h4 style={{ marginBottom: '10px', color: '#10b981' }}>📦 Material Cost</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
              <div className="form-group">
                <label>Amount</label>
                <input type="number" step="0.01" value={detailData.materialcost}
                  onChange={(e) => setDetailData({ ...detailData, materialcost: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Received</label>
                <input type="number" step="0.01" value={detailData.mc_received}
                  onChange={(e) => setDetailData({ ...detailData, mc_received: e.target.value })} 
                  style={{ fontWeight: 'bold' }} />
              </div>
              <div className="form-group">
                <label>Mode</label>
                <select value={detailData.mc_paymentmode}
                  onChange={(e) => setDetailData({ ...detailData, mc_paymentmode: e.target.value })}>
                  <option value="">Select</option>
                  {paymentModes.map(m => <option key={m.modecode} value={m.modecode}>{m.modename}</option>)}
                </select>
              </div>
            </div>
            {detailData.mc_paymentmode === 'CHQ' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '10px', background: '#d1fae5', padding: '10px', borderRadius: '5px' }}>
                <input placeholder="Bank Name" value={detailData.mc_bankname}
                  onChange={(e) => setDetailData({ ...detailData, mc_bankname: e.target.value })} />
                <input placeholder="Cheque No" value={detailData.mc_chequeno}
                  onChange={(e) => setDetailData({ ...detailData, mc_chequeno: e.target.value })} />
                <input type="date" value={detailData.mc_chequedate}
                  onChange={(e) => setDetailData({ ...detailData, mc_chequedate: e.target.value })} />
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px', marginTop: '15px' }}>
            <div className="form-group">
              <label>Delivery Time</label>
              <input type="datetime-local" value={detailData.receivedtime}
                onChange={(e) => setDetailData({ ...detailData, receivedtime: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Remarks</label>
              <input type="text" value={detailData.remarks}
                onChange={(e) => setDetailData({ ...detailData, remarks: e.target.value })} />
            </div>
          </div>

          <div style={{ marginTop: '15px', padding: '10px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: '8px', color: '#fff', display: 'flex', justifyContent: 'space-between' }}>
            <span>Current Total:</span>
            <span style={{ fontSize: '20px', fontWeight: 'bold' }}>{calculateDetailTotal().toFixed(2)} SAR</span>
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button className="btn-secondary" onClick={resetDetailForm}>
              <X size={18} /> Clear
            </button>
            <button className="btn-primary" onClick={handleAddDetail}>
              {editingDetail ? <><Edit2 size={18} /> Update</> : <><Plus size={18} /> Add</>}
            </button>
          </div>
        </div>
      )}

      {/* Details Grid */}
      <div style={{ background: '#fff', padding: '20px', borderRadius: '8px' }}>
        <h3 style={{ marginBottom: '20px' }}>DRS Details ({details.length})</h3>
        {details.length === 0 ? (
          <div className="empty-state"><p>No details added yet</p></div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>AWB</th>
                  <th>CC</th>
                  <th>MC</th>
                  <th>CC Rcvd</th>
                  <th>MC Rcvd</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Receiver</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {details.map((d, i) => (
                  <tr key={i}>
                    <td className="font-mono">{d.awbno}</td>
                    <td className="text-right">{parseFloat(d.couriercharge).toFixed(2)}</td>
                    <td className="text-right">{parseFloat(d.materialcost).toFixed(2)}</td>
                    <td className="text-right" style={{ fontWeight: 'bold', color: '#10b981' }}>
                      {parseFloat(d.cc_received).toFixed(2)}
                    </td>
                    <td className="text-right" style={{ fontWeight: 'bold', color: '#10b981' }}>
                      {parseFloat(d.mc_received).toFixed(2)}
                    </td>
                    <td className="text-right" style={{ fontWeight: 'bold', color: '#667eea' }}>
                      {(parseFloat(d.cc_received) + parseFloat(d.mc_received)).toFixed(2)}
                    </td>
                    <td style={{ fontSize: '11px' }}>
                      {d.cc_paymentmode && <span className="type-badge" style={{ background: '#3b82f6', marginRight: '3px' }}>
                        CC:{getPaymentModeName(d.cc_paymentmode)}</span>}
                      {d.mc_paymentmode && <span className="type-badge" style={{ background: '#10b981' }}>
                        MC:{getPaymentModeName(d.mc_paymentmode)}</span>}
                    </td>
                    <td>{d.receivername || '-'}</td>
                    <td><span className={`status-badge ${d.deliverystatus === 'DL' ? 'active' : 'inactive'}`}>
                      {getDeliveryStatusName(d.deliverystatus)}</span></td>
                    <td className="actions-column">
                      {mode !== 'view' && (<>
                        <button className="btn-icon btn-edit" onClick={() => handleEditDetail(d, i)}><Edit2 size={14} /></button>
                        <button className="btn-icon btn-delete" onClick={() => handleDeleteDetail(i)}><Trash2 size={14} /></button>
                      </>)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb', fontWeight: 'bold' }}>
                  <td colSpan="5" style={{ textAlign: 'right' }}>Grand Total:</td>
                  <td className="text-right" style={{ fontSize: '18px', color: '#667eea' }}>
                    {drsData.totalamount.toFixed(2)} SAR
                  </td>
                  <td colSpan="4"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DRSTransactionEntry;